<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

interface Props {
  type?: 'button' | 'submit' | 'reset'
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  loading?: boolean
  disabled?: boolean
  icon?: string
  iconOnlyLabel?: string
  to?: RouteLocationRaw
  compact?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  variant: 'primary',
  loading: false,
  disabled: false,
  compact: false
})

const buttonClasses = computed(() => ['tq-button', `tq-button--${props.variant}`, { 'tq-button--loading': props.loading, 'tq-button--compact': props.compact }])

function preventUnavailableNavigation(event: MouseEvent) {
  if (props.disabled || props.loading) event.preventDefault()
}
</script>

<template>
  <NuxtLink
    v-if="props.to"
    :to="props.to"
    :class="buttonClasses"
    :aria-busy="props.loading"
    :aria-disabled="props.disabled || props.loading"
    :aria-label="props.iconOnlyLabel"
    @click="preventUnavailableNavigation"
  >
    <span v-if="props.loading" class="tq-button__spinner" aria-hidden="true" />
    <span class="tq-button__content">
      <UIcon v-if="props.icon && !props.loading" :name="props.icon" aria-hidden="true" />
      <slot />
    </span>
  </NuxtLink>
  <button
    v-else
    :type="props.type"
    :class="buttonClasses"
    :disabled="props.disabled || props.loading"
    :aria-busy="props.loading"
    :aria-label="props.iconOnlyLabel"
  >
    <span v-if="props.loading" class="tq-button__spinner" aria-hidden="true" />
    <span class="tq-button__content">
      <UIcon v-if="props.icon && !props.loading" :name="props.icon" aria-hidden="true" />
      <slot />
    </span>
  </button>
</template>

<style scoped>
.tq-button {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  border: 0;
  border-radius: 0.78rem;
  padding: 0.75rem 1.1rem;
  cursor: pointer;
  font-weight: var(--tq-font-weight-control);
  letter-spacing: -0.015em;
  box-shadow: none;
  transition: transform 180ms var(--tq-ease-out), background 180ms var(--tq-ease-out), color 180ms var(--tq-ease-out), box-shadow 180ms var(--tq-ease-out);
  text-decoration: none;
}
.tq-button:focus-visible { outline: 2px solid var(--tq-focus); outline-offset: 3px; box-shadow: none; }
.tq-button:hover:not(:disabled):not([aria-disabled="true"]) { transform: translateY(-2px); box-shadow: var(--tq-shadow-tight); }
.tq-button:disabled { cursor: not-allowed; opacity: 0.65; }
.tq-button[aria-disabled="true"] { cursor: not-allowed; opacity: 0.65; }
.tq-button--compact { min-height: 2.15rem; border-radius: var(--tq-radius-md); padding: .42rem .72rem; font-size: .78rem; }
.tq-button--primary { border: 1px solid var(--tq-action-border); background-color: var(--tq-action); background-image: var(--tq-gradient-action); color: var(--tq-on-action); }
.tq-button--primary:hover:not(:disabled):not([aria-disabled="true"]) { background-color: color-mix(in oklch, var(--tq-action) 88%, var(--tq-color-teal)); filter: brightness(1.08); }
.tq-button--secondary { border: 1px solid var(--tq-secondary-action-border); background: var(--tq-secondary-action); color: var(--tq-on-secondary-action); box-shadow: inset 0 1px color-mix(in oklch, white 42%, transparent); }
.tq-button--secondary:hover:not(:disabled):not([aria-disabled="true"]) { background: color-mix(in oklch, var(--tq-secondary-action) 88%, var(--tq-action)); }
.tq-button--ghost { border: 1px solid var(--tq-ghost-action-border); background: var(--tq-ghost-action); color: var(--tq-on-ghost-action); }
.tq-button--ghost:hover:not(:disabled):not([aria-disabled="true"]) { background: color-mix(in oklch, var(--tq-ghost-action) 88%, var(--tq-on-ghost-action)); color: var(--tq-on-ghost-action); }
.tq-button--danger { border: 1px solid var(--tq-danger-border); background: var(--tq-danger); color: var(--tq-on-danger); }
.tq-button--danger:hover:not(:disabled):not([aria-disabled="true"]) { background: color-mix(in oklch, var(--tq-danger) 86%, var(--tq-text)); }
.tq-button__content { display: inline-flex; align-items: center; gap: 0.5rem; }
.tq-button__spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: tq-spin 800ms linear infinite;
}
@keyframes tq-spin { to { transform: rotate(1turn); } }
</style>
